import React, { useState } from "react";
import GoogleIcon from "../sharedComponents/GoogleIcon";
import InputField from "../sharedComponents/InputField";
import PhoneInputField from "../sharedComponents/PhoneInputField";
import { EnvelopeIcon, LockClosedIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { signupUser } from "../../slices/1-auth/1-login/thunk";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/Logo.webp";
import { useTranslation } from "react-i18next";
import MainButton from "../sharedComponents/MainButton";
import { validateField } from "../../utils/validateForm";
import { validationRegex } from "../../utils/validationRegex";

const SignForm = () => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  /* ================= STATIC CONTENT ================= */
  const formText = {
    title: t("signup.title"),
    subtitle: t("signup.subtitle"),
    firstNameLabel: t("signup.firstNameLabel"),
    firstNamePlaceholder: t("signup.firstNamePlaceholder"),
    lastNameLabel: t("signup.lastNameLabel"),
    lastNamePlaceholder: t("signup.lastNamePlaceholder"),
    emailLabel: t("signup.emailLabel"),
    emailPlaceholder: t("signup.emailPlaceholder"),
    phoneLabel: t("signup.phoneLabel"),
    passwordLabel: t("signup.passwordLabel"),
    passwordPlaceholder: t("signup.passwordPlaceholder"),
    confirmPasswordLabel: t("signup.confirmPasswordLabel"),
    confirmPasswordPlaceholder: t("signup.confirmPasswordPlaceholder"),
    termsText: t("signup.termsText"),
    termsLinkText: t("signup.termsLinkText"),
    termsLinkHref: "/terms-condition",
    forgotPasswordText: t("signup.forgotPasswordText"),
    signUpText: t("signup.signUpText"),
    creatingAccountText: t("signup.creatingAccountText"),
    orText: t("signup.orText"),
    googleSignUpText: t("signup.googleSignUpText"),
    additionalText: t("signup.additionalText"),
    additionalLinkText: t("signup.additionalLinkText"),
    additionalLinkHref: "/login",
    passwordRules: {
      minLength: t("signup.passwordRules.minLength"),
      lowercase: t("signup.passwordRules.lowercase"),
      uppercase: t("signup.passwordRules.uppercase"),
      number: t("signup.passwordRules.number"),
    },
    fillAllFieldsText: t("signup.fillAllFieldsText"),
    passwordsMismatchText: t("signup.passwordsMismatchText"),
    acceptTermsText: t("signup.acceptTermsText"),
    accountCreationFailedText: t("signup.accountCreationFailedText"),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      firstName: validateField("firstName", firstName, validationRegex.signup.firstName, t),
      lastName: validateField("lastName", lastName, validationRegex.signup.lastName, t),
      email: validateField("email", email, validationRegex.signup.email, t),
      password: validateField("password", password, validationRegex.signup.password, t),
      phone: validateField("phone", phone, validationRegex.signup.phone, t),
    };

    if (!confirmPassword) {
      newErrors.confirmPassword = t("validation.required");
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t("validation.passwordMismatch");
    }

    if (!acceptedTerms) {
      newErrors.acceptedTerms = t("validation.acceptTerms");
    }

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) return;

    const userData = { firstName, lastName, email, password, phone };

    try {
      await dispatch(signupUser(userData)).unwrap();
      toast.success(t("signup.accountCreatedSuccessText"));
      navigate("/otp");
    } catch (err) {
      toast.error(err || formText.accountCreationFailedText);
    }
  };
  // console.log(phone)
  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Left Side */}
      <div className="flex-col items-center justify-center hidden min-h-screen text-center md:flex dark:bg-bg-dark-secondary bg-bg-light-secondary">
        <img
          src={logo}
          alt={t("signup.logoAlt")}
          className="flex items-center justify-center mb-6 rounded-full w-28 h-28 shadow-glow"
        />
        <h1 className="mb-4 text-3xl font-bold text-black dark:text-white">{t("signup.brandName")}</h1>
        <p className="px-4 text-center text-text-muted-light dark:text-text-muted-dark">{t("signup.brandSubtitle")}</p>
      </div>

      {/* Right Form */}
      <div className="flex flex-col justify-center max-w-lg mx-auto text-center md:my-6 md:gap-2">
        <h2 className="text-2xl font-bold text-center md:text-3xl">{formText.title}</h2>
        <p className="text-center md:text-md text-text-muted-light dark:text-text-muted-dark">{formText.subtitle}</p>

        <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
          {/* First & Last Name */}
          <div className="flex gap-4">
            <div className="flex flex-col w-1/2 gap-1 text-start">
              <label>{formText.firstNameLabel}</label>
              <InputField type="text" placeholder={formText.firstNamePlaceholder} icon={EnvelopeIcon} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              {errors.firstName && (
                <p className="text-xs text-red-500">{errors.firstName}</p>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-1 text-start">
              <label>{formText.lastNameLabel}</label>
              <InputField type="text" placeholder={formText.lastNamePlaceholder} icon={EnvelopeIcon} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              {errors.lastName && (
                <p className="text-xs text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1 text-start">
            <label>{formText.emailLabel}</label>
            <InputField type="email" placeholder={formText.emailPlaceholder} icon={EnvelopeIcon} value={email} onChange={(e) => setEmail(e.target.value)} />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="flex flex-col gap-1 text-start">
            <label>{formText.phoneLabel}</label>
            <PhoneInputField value={phone} onChange={setPhone} />
            {errors.phone && (
              <p className="text-xs text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1 text-start">
            <label>{formText.passwordLabel}</label>
            <InputField type="password" placeholder={formText.passwordPlaceholder} icon={LockClosedIcon} value={password} onChange={(e) => setPassword(e.target.value)} />
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Password Rules */}
          {/* <div className="flex gap-4 my-2">
            <div className="flex flex-col w-1/2 gap-1 text-start">
              <label className="text-sm dark:text-text-muted-dark text-text-muted-light"><XMarkIcon className="inline w-4 h-4 dark:text-text-muted-dark text-text-muted-light me-1" />{formText.passwordRules.minLength}</label>
              <label className="text-sm dark:text-text-muted-dark text-text-muted-light"><XMarkIcon className="inline w-4 h-4 dark:text-text-muted-dark text-text-muted-light me-1" />{formText.passwordRules.lowercase}</label>
            </div>
            <div className="flex flex-col w-1/2 gap-1 text-start">
              <label className="text-sm dark:text-text-muted-dark text-text-muted-light"><XMarkIcon className="inline w-4 h-4 dark:text-text-muted-dark text-text-muted-light me-1" />{formText.passwordRules.uppercase}</label>
              <label className="text-sm dark:text-text-muted-dark text-text-muted-light"><XMarkIcon className="inline w-4 h-4 dark:text-text-muted-dark text-text-muted-light me-1" />{formText.passwordRules.number}</label>
            </div>
          </div> */}

          {/* Confirm Password */}
          <div className="flex flex-col gap-1 text-start">
            <label>{formText.confirmPasswordLabel}</label>
            <InputField type="password" placeholder={formText.confirmPasswordPlaceholder} icon={LockClosedIcon} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            {errors.confirmPassword && (
              <p className="text-xs text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Terms */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="appearance-none w-4 h-4 border border-gray-300 rounded-sm dark:checked:bg-primary checked:bg-primary-light dark:checked:border-primary checked:border-primary-light relative
              before:content-['✓'] before:absolute before:text-dark before:top-[-3px] before:left-[2px] before:opacity-0 checked:before:opacity-100" />
              {formText.termsText}{" "}
              <a href={formText.termsLinkHref} className="dark:text-primary text-primary-light">{formText.termsLinkText}</a>
              {errors.acceptedTerms && (
                <p className="text-xs text-red-500">{errors.acceptedTerms}</p>
              )}
            </label>
          </div>

          {/* Submit Button */}
          <MainButton type="submit" disabled={loading} className="flex items-center justify-center py-3 text-lg font-semibold transition duration-300 dark:bg-primary bg-primary-light dark:text-bg-dark-primary text-bg-light-primary rounded-xl disabled:opacity-70 shadow-glow hover:shadow-glow-hover ">
            {loading ? formText.creatingAccountText : formText.signUpText}
          </MainButton>

          {/* OR Divider */}
          <div className="flex items-center my-2">
            <hr className="flex-grow border-gray-300" />
            <span className="mx-2 text-gray-400">{formText.orText}</span>
            <hr className="flex-grow border-gray-300" />
          </div>

          {/* Google Sign Up */}
          <button type="button" onClick={() => window.location.replace(`${process.env.REACT_APP_API_URL}/google`)} className="flex items-center justify-center w-full gap-2 px-4 py-3 duration-300 border dark:border-primary border-primary-light dark:text-primary text-primary-light rounded-xl dark:hover:bg-primary hover:bg-primary-light dark:hover:text-bg-dark-primary hover:text-bg-light-primary">
            <GoogleIcon />
            <span className="flex-grow text-center">{formText.googleSignUpText}</span>
            <span className="w-5"></span>
          </button>

          {/* Additional Text */}
          {formText.additionalText && (
            <p className="mt-2 text-sm text-center">
              {formText.additionalText}{" "}
              <a href={formText.additionalLinkHref} className="dark:text-primary text-primary-light hover:underline">{formText.additionalLinkText}</a>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default SignForm;
